"use client";

import { useCallback, useEffect, useRef } from "react";

/**
 * Global ref counter for body scroll locks
 * Prevents conflicts when multiple components need to lock scroll
 */
let scrollLockCount = 0;
let originalBodyOverflow: string | null = null;
let originalHtmlOverflow: string | null = null;

function applyScrollLock() {
  originalBodyOverflow = document.body.style.overflow || "";
  originalHtmlOverflow = document.documentElement.style.overflow || "";
  document.body.style.overflow = "hidden";
  // `html { overflow-x: clip }` in globals.css computes overflow-y to auto, so
  // html (not body) is the document scroller. Lock both.
  document.documentElement.style.overflow = "hidden";
}

function restoreScrollLock() {
  document.body.style.overflow = originalBodyOverflow || "";
  document.documentElement.style.overflow = originalHtmlOverflow || "";
  originalBodyOverflow = null;
  originalHtmlOverflow = null;
}

/**
 * Custom hook for managing body scroll lock with ref counting
 * Prevents conflicts when multiple components need to lock/unlock scroll
 * @returns Object with lock and unlock functions
 */
export function useBodyScrollLock() {
  const lockRef = useRef(false);

  const lock = useCallback(() => {
    if (!lockRef.current && typeof document !== "undefined") {
      lockRef.current = true;
      scrollLockCount++;
      if (scrollLockCount === 1) {
        applyScrollLock();
      }
    }
  }, []);

  const unlock = useCallback(() => {
    if (lockRef.current && typeof document !== "undefined") {
      lockRef.current = false;
      scrollLockCount--;
      if (scrollLockCount === 0) {
        restoreScrollLock();
      }
      // Prevent negative count (safety check)
      if (scrollLockCount < 0) {
        scrollLockCount = 0;
      }
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (lockRef.current) {
        unlock();
      }
    };
  }, [unlock]);

  return { lock, unlock };
}
